import { supabase } from "@/integrations/supabase/client";
import { assertWriteLanded } from "@/lib/scheduledTaskEdit";
import { nextRunAtByInterval } from "@/lib/scheduleNextRun";

/** One constant so the stored schedule_time and the computed
 *  next_run_at cannot drift apart again (#553). */
const SIZE_CHECK_TIME = "09:00";

// v1.3 spec §B.8 Write 2 / issue H4: creates reminder rows in the existing
// "appointment_tracker" scheduled_tasks type — same shape as
// buildAppointmentFromForm()/submitAppointment() in TasksTab.tsx (introspected,
// not invented: same task_type string, same config JSONB shape, same
// per-item field set). That form's APPOINTMENT_TYPES has no "pediatrician"
// key, so pediatrician reminders use category "doctor" (closest existing
// key) with name "Doctor" — exactly what a mom picking "Doctor" in the
// existing form would get.
//
// next_run_at: the existing form always sets a NEW appointment_tracker row's
// next_run_at to "now" — it's a daily-cadence row (interval_days: 1); each
// item's own last_done/interval_days governs actual due-ness, re-evaluated
// per item on every daily run (confirmed against product_reorder, which
// follows the identical "row checks daily, items carry real cadence"
// pattern). So "first next_run_at = now + cadence" from spec §B.8 is
// achieved here via each new item's own `last_done` = today, which makes
// that item due ~cadence months out — not by offsetting the row-level
// next_run_at, which would incorrectly delay the row's next daily check for
// every other appointment already on it.

// #672 — a real last-visit date instead of always assuming "just seen".
// Pills, not a date picker: the Providers card is meant to take seconds, and
// "over 6 months ago" is exactly as actionable to the scheduler as an exact
// date would be. daysAgo values are rough midpoints of what each label
// promises, not precise — "a few months" covers 2-4, so 90 days is the
// honest middle, not a guess dressed up as one.
export type LastVisitPill = "recent" | "few_months" | "over_6mo" | "unsure";

const LAST_VISIT_DAYS_AGO: Record<Exclude<LastVisitPill, "unsure">, number> = {
  recent: 15, // "within the last month"
  few_months: 90, // "a few months ago"
  over_6mo: 210, // "over 6 months ago" — clearly past 6mo, not an arbitrary jump
};

/** "unsure" (or no pill picked at all) keeps the original behavior: today. */
export function lastVisitDateIso(pill: LastVisitPill | undefined, todayIso: string): string {
  if (!pill || pill === "unsure") return todayIso;
  const d = new Date(`${todayIso}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() - LAST_VISIT_DAYS_AGO[pill]);
  return d.toISOString().slice(0, 10);
}

export type ReminderInput = {
  category: "dentist" | "doctor";
  label: string;
  providerName: string;
  cadenceMonths: number;
  lastVisitPill?: LastVisitPill;
  // #672 — who this reminder is for, when Kin actually knows (the Providers
  // card itself never collects a kid association — see the caller in
  // ProSetupFlowModal.tsx for how this gets inferred). Falls back to "the
  // crew" below when absent, same as before this issue.
  kidName?: string;
};

// The actual nudge fires remind_days_before days ahead of an item's due date
// (last_done + interval_days) — same field every item carries, read back at
// run-scheduled-tasks time. Kept as one constant so buildAppointmentItem and
// nudgeDateIso can never drift apart on what "first nudge" means.
const REMIND_DAYS_BEFORE = 14;

function buildAppointmentItem(input: ReminderInput, lastDoneIso: string): Record<string, unknown> {
  return {
    id: crypto.randomUUID().slice(0, 8),
    name: input.label,
    category: input.category,
    interval_days: input.cadenceMonths * 30,
    last_done: lastDoneIso,
    remind_days_before: REMIND_DAYS_BEFORE,
    who_for: input.kidName || "the crew",
    provider_name: input.providerName,
    provider_phone: null,
    completion_history: [],
    learned_interval: null,
  };
}

/**
 * #673 — the Pro Setup Close card names each reminder with a real date. This
 * must derive from the exact fields buildAppointmentItem just wrote (a date-only
 * "YYYY-MM-DD" last_done), not be recomputed some other way that could drift.
 */
export function nudgeDateIso(lastDoneIso: string, intervalDays: number, remindDaysBefore: number): string {
  const d = new Date(`${lastDoneIso}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + intervalDays - remindDaysBefore);
  return d.toISOString();
}

/** "Feb 14" — UTC-anchored so CI (which runs UTC) and a phone in any US timezone agree. */
export function formatShortDateUTC(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}

export type CreatedAppointmentReminder = {
  category: "dentist" | "doctor";
  label: string;
  nudgeDateIso: string;
};

export type CreateAppointmentRemindersResult = {
  taskId: string;
  reminders: CreatedAppointmentReminder[];
};

// Both helpers below THROW on a failed write rather than resolving quietly.
// supabase-js resolves { data, error } on a DB/RLS failure instead of
// rejecting, so an unread `error` means the caller's await succeeds, its
// try/catch never fires, and it goes on to print "✓ Reminder set" for a row
// that does not exist. Every caller already wraps these in a try/catch, so
// throwing is what lets that catch do its job. Same reasoning as #416 /
// handleKidNamesNext, and house rule 16: never swallow. As of #673 the
// insert/update also .select("id") and assertWriteLanded — an RLS-filtered
// write that matches zero rows resolves with error === null, and the id is
// now load-bearing (the Close card deep-links to it), not just a receipt line.
/**
 * #1528 — the identity of an appointment item, as SHE controls it.
 *
 * Deliberately not the generated `id`: `buildAppointmentItem` mints a fresh
 * `crypto.randomUUID().slice(0, 8)` on every call, so two attempts at the same
 * dentist are two different ids and an id-keyed merge would append both. The
 * caller controls category and provider name; those are the identity.
 *
 * Compared loosely (trim + lowercase) for the same reason plant names are —
 * "Dr. Ruiz" and "dr. ruiz " are one provider to her. A missing provider name
 * normalises to "", so two same-category reminders with no provider named are
 * treated as one. That is the right answer for a retry and the honest cost of
 * matching on what the caller actually supplies.
 */
export function appointmentMatchKey(category: unknown, providerName: unknown): string {
  const norm = (v: unknown) => (typeof v === "string" ? v.trim().toLowerCase() : "");
  return `${norm(category)}\u0000${norm(providerName)}`;
}

/**
 * #1528 — fold `incoming` into `existing`, replacing any item that matches by
 * {@link appointmentMatchKey} instead of adding a second one.
 *
 * ⚠️ An item's ACCUMULATED state survives the merge: `id`, `completion_history`
 * and `learned_interval` belong to the row's history, not to this call.
 * Overwriting them wholesale would turn a harmless retry into silent data loss
 * — a fresh item carries `completion_history: []`, so a naive replace would
 * erase every visit she has ticked off since the reminder was created.
 *
 * Pure and exported so the merge rule is testable without a database.
 */
export function mergeAppointmentItems(
  existingItems: unknown,
  incoming: Record<string, unknown>[],
): Record<string, unknown>[] {
  const merged: Record<string, unknown>[] = Array.isArray(existingItems)
    ? existingItems.map((i) => i as Record<string, unknown>)
    : [];
  const indexByKey = new Map<string, number>();
  merged.forEach((item, i) => {
    indexByKey.set(appointmentMatchKey(item?.category, item?.provider_name), i);
  });
  for (const item of incoming) {
    const key = appointmentMatchKey(item.category, item.provider_name);
    const at = indexByKey.get(key);
    if (at === undefined) {
      indexByKey.set(key, merged.length);
      merged.push(item);
      continue;
    }
    const prev = merged[at] || {};
    merged[at] = {
      ...item,
      id: prev.id ?? item.id,
      completion_history: prev.completion_history ?? item.completion_history,
      learned_interval: prev.learned_interval ?? item.learned_interval,
    };
  }
  return merged;
}

export async function createAppointmentReminders(
  userId: string,
  inputs: ReminderInput[]
): Promise<CreateAppointmentRemindersResult> {
  if (inputs.length === 0) return { taskId: "", reminders: [] };
  const todayIso = new Date().toISOString().slice(0, 10);
  // #672 — each provider can have its OWN last-visit answer, so this is
  // resolved per item, not once for the whole batch the way todayIso was.
  const lastDones = inputs.map((input) => lastVisitDateIso(input.lastVisitPill, todayIso));
  const newItems = inputs.map((input, i) => buildAppointmentItem(input, lastDones[i]));
  const reminders: CreatedAppointmentReminder[] = inputs.map((input, i) => ({
    category: input.category,
    label: input.label,
    nudgeDateIso: nudgeDateIso(lastDones[i], input.cadenceMonths * 30, REMIND_DAYS_BEFORE),
  }));

  const { data: existingTask, error: selectError } = await (supabase as any)
    .from("scheduled_tasks")
    .select("id, config")
    .eq("user_id", userId)
    .eq("task_type", "appointment_tracker")
    .maybeSingle();
  // A failed read here is not "no existing row" — treating it as one would
  // insert a second appointment_tracker and split her appointments across two.
  if (selectError) throw selectError;

  if (existingTask) {
    // #1528 — MERGE, never append. This used to write
    // `[...existing.config.items, ...newItems]`, so calling it twice with the
    // same providers gave her the same dentist twice in one tracker, with
    // nothing on the card to tell them apart. #1478 wanted to offer a retry
    // here and could not, for exactly that reason.
    const items = mergeAppointmentItems(existingTask.config?.items, newItems);
    const newConfig = { ...(existingTask.config || {}), items };
    const { data, error } = await (supabase as any)
      .from("scheduled_tasks")
      .update({ config: newConfig, updated_at: new Date().toISOString() })
      .eq("id", existingTask.id)
      .select("id");
    if (error) throw error;
    assertWriteLanded(data, "appointment reminder");
    return { taskId: existingTask.id, reminders };
  }

  const { data, error } = await (supabase as any)
    .from("scheduled_tasks")
    .insert({
      user_id: userId,
      task_type: "appointment_tracker",
      display_name: "Appointments",
      schedule_day: null,
      schedule_time: "08:00",
      interval_days: 1,
      is_active: true,
      config: { items: newItems },
      next_run_at: new Date().toISOString(),
    })
    .select("id");
  if (error) throw error;
  assertWriteLanded(data, "appointment reminder");
  return { taskId: data[0].id, reminders };
}

// v1.3 spec §B.11 Write 2 / issue H4: size_check is a brand-new task type
// (added to the scheduled_tasks_task_type_check constraint by the
// already-merged v1.3 schema migration) — there's no existing form to
// introspect for it, unlike appointment_tracker. Spec gives task_type,
// next_run_at (now + interval), and config shape directly. This is a real
// standalone recurring unit (one row per kid), not a daily-recheck-array
// type, so row-level interval_days/next_run_at carry the real cadence —
// matching how genuinely periodic single-purpose types (e.g. weekly_wins)
// are inserted elsewhere in this file, not the appointment_tracker pattern.
// display_name/schedule_time aren't specified by spec; chosen to match the
// convention every other scheduled_tasks insert in the codebase follows.
export type CreateSizeCheckTaskResult = { taskId: string; firstCheckDateIso: string };

/**
 * #553 — `next_run_at` is her `schedule_time`, `intervalDays` out, in HER
 * timezone. It used to be `Date.now() + intervalDays * 86400000`, which
 * silently carried the creation-moment clock time: a row created at 22:39
 * fired at 22:39 regardless of the 09:00 written into schedule_time on the
 * very next line. The #501 quiet-hours floor then suppressed the text, so she
 * got a card in the app, no text, and nothing explaining the difference. Two
 * production rows were found in exactly that state.
 *
 * ⚠️ Deliberately NOT the same call as createAppointmentReminders above. That
 * one is right to use "now": it is a daily-cadence row (interval_days: 1)
 * whose ITEMS carry the real cadence via last_done, so offsetting the row
 * would delay the daily check for every appointment already on it. size_check
 * is the opposite shape — one row per kid, the row's own interval IS the
 * cadence — so "now" there is a defect and here it was not.
 */
/**
 * #1528 — the active size_check row for this kid, or undefined.
 *
 * Exact match on `config.kid`, deliberately: `TasksTab.submitSizeCheck` decides
 * whether a row belongs to a kid with `editingRow.config?.kid !== kid`, and
 * these two must agree about what "the same kid" means. A looser comparison
 * here would let this function update a row TasksTab would treat as a
 * different kid's, which is how one row per kid stops being one row per kid.
 *
 * Pure and exported so the matching rule is testable without a database.
 */
export function findSizeCheckForKid<T extends { config?: { kid?: unknown } | null }>(
  rows: readonly T[] | null | undefined,
  kid: string,
): T | undefined {
  return (rows || []).find((r) => r?.config?.kid === kid);
}

export async function createSizeCheckTask(
  userId: string,
  kid: string,
  intervalDays = 42,
  timezone: string | null = null
): Promise<CreateSizeCheckTaskResult> {
  // #1528 — look before inserting. This used to be a bare insert with no
  // lookup at all, and size_check's invariant is one row per kid:
  // TasksTab.submitSizeCheck refuses to merge two, saying so, because
  // "merging would silently destroy one of the two rows". A second row is a
  // state the rest of the app has already decided it will not repair, so a
  // retried setup could leave her with two reminders and no way to tell which
  // to delete.
  const { data: existingRows, error: selectError } = await (supabase as any)
    .from("scheduled_tasks")
    .select("id, config, schedule_time")
    .eq("user_id", userId)
    .eq("task_type", "size_check")
    .eq("is_active", true);
  // A failed read is NOT "no existing row" — same reasoning as
  // createAppointmentReminders above. Treating a transport error as absence is
  // precisely what inserts the duplicate this function exists to avoid.
  if (selectError) throw selectError;
  const existing = findSizeCheckForKid(existingRows as { id: string; config?: { kid?: unknown } | null; schedule_time?: string | null }[], kid);

  if (existing) {
    // Her existing schedule_time wins over this function's default: the row
    // already fires at a time she may have chosen, and #553 is about
    // next_run_at agreeing with schedule_time rather than with the clock.
    const nextRunAtExisting = nextRunAtByInterval(
      intervalDays,
      existing.schedule_time || SIZE_CHECK_TIME,
      timezone,
    );
    const { data, error } = await (supabase as any)
      .from("scheduled_tasks")
      .update({
        interval_days: intervalDays,
        config: { ...((existing.config as Record<string, unknown>) || {}), kid, interval_days: intervalDays },
        next_run_at: nextRunAtExisting,
        is_active: true,
        updated_at: new Date().toISOString(),
      })
      .eq("id", existing.id)
      .select("id");
    if (error) throw error;
    // An RLS-filtered UPDATE that matches zero rows returns 200 with
    // error === null (house rule 16), so the error check above is not enough.
    assertWriteLanded(data, "size check-in");
    return { taskId: existing.id, firstCheckDateIso: nextRunAtExisting };
  }

  const nextRunAt = nextRunAtByInterval(intervalDays, SIZE_CHECK_TIME, timezone);
  const { data, error } = await (supabase as any)
    .from("scheduled_tasks")
    .insert({
      user_id: userId,
      task_type: "size_check",
      display_name: `${kid}'s sizes`,
      schedule_day: null,
      schedule_time: SIZE_CHECK_TIME,
      interval_days: intervalDays,
      is_active: true,
      config: { kid, interval_days: intervalDays },
      next_run_at: nextRunAt,
    })
    .select("id");
  if (error) throw error;
  assertWriteLanded(data, "size check-in");
  return { taskId: data[0].id, firstCheckDateIso: nextRunAt };
}

// #696 — Garden Planner offers to turn its generated plan into real
// plant_care reminders. Row/item shape introspected from the existing manual
// "+ Add plant" form (TasksTab.tsx: buildPlantFromForm/submitPlantCare), NOT
// invented — same task_type, same config.{plants,items} wrapper, same
// per-item field set (id/name/category/interval_days/last_done/
// remind_days_before/plant_name/completion_history/learned_interval), same
// select-existing-row-then-update-or-insert pattern as
// createAppointmentReminders above (plant_care is also a single shared row
// per user, not one row per plant).
//
// Idempotent per plant name as of the #696 verify pass — see the dedupe block
// inside the function for why that is load-bearing rather than a nicety.
//
// Water-only, deliberately: the generated plan has no fertilize-cadence data
// (or any per-crop cadence data at all — see the PR body for why the default
// interval below is a starting SUGGESTION she can edit before confirming,
// not a fact this function invented and presented as gardening authority).
export type PlantCareInput = {
  plantName: string;
  waterIntervalDays: number;
};

export type CreatedPlantCareItem = {
  plantName: string;
  waterIntervalDays: number;
};

export type CreatePlantCareRemindersResult = {
  taskId: string;
  /** Only the plants this call actually wrote. */
  items: CreatedPlantCareItem[];
  /** Plants left alone because plant_care already tracks them (original casing). */
  skipped: string[];
};

/**
 * #696 verify pass — plant names compare loosely. The offer's names come from
 * a generated plan and the existing ones from a form she typed by hand, so
 * "Tomatoes" and "tomatoes " are the same plant to her and must be the same
 * plant to us. Trim + lowercase is as far as this goes deliberately: anything
 * fuzzier (singular/plural, "Cherry Tomatoes" vs "Tomatoes") would start
 * silently dropping reminders she genuinely asked for.
 */
export function normalizePlantName(name: string): string {
  return name.trim().toLowerCase();
}

/**
 * Every plant name a plant_care config already knows about, read from BOTH
 * `plants` (the meta list the detail UI renders) and `items` (the per-care-task
 * rows the scheduler reads). They are written together by the manual form, but
 * reading both means an item that outlived its meta entry — or vice versa —
 * still counts as "already tracked". Over-detecting here costs one unchecked
 * box; under-detecting costs a duplicate reminder.
 */
export function plantNamesFromConfig(config: unknown): string[] {
  const cfg = (config || {}) as { plants?: Array<{ name?: unknown }>; items?: Array<{ plant_name?: unknown }> };
  const names = [
    ...(Array.isArray(cfg.plants) ? cfg.plants.map((p) => p?.name) : []),
    ...(Array.isArray(cfg.items) ? cfg.items.map((i) => i?.plant_name) : []),
  ].filter((n): n is string => typeof n === "string" && n.trim().length > 0);
  const seen = new Set<string>();
  return names.filter((n) => {
    const key = normalizePlantName(n);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * #696 verify pass — what the Garden Planner offer needs to know before it
 * renders: which crops she is already being reminded about. Throws on a read
 * failure (the caller decides whether that is fatal); a missing plant_care row
 * is simply an empty list, not an error.
 */
export async function fetchTrackedPlantNames(userId: string): Promise<string[]> {
  const { data, error } = await (supabase as any)
    .from("scheduled_tasks")
    .select("config")
    .eq("user_id", userId)
    .eq("task_type", "plant_care")
    .maybeSingle();
  if (error) throw error;
  return plantNamesFromConfig(data?.config);
}

export async function createPlantCareReminders(
  userId: string,
  inputs: PlantCareInput[]
): Promise<CreatePlantCareRemindersResult> {
  if (inputs.length === 0) return { taskId: "", items: [], skipped: [] };

  const { data: existingTask, error: selectError } = await (supabase as any)
    .from("scheduled_tasks")
    .select("id, config")
    .eq("user_id", userId)
    .eq("task_type", "plant_care")
    .maybeSingle();
  // A failed read here is not "no existing row" — treating it as one would
  // insert a second plant_care row and split her reminders across two.
  if (selectError) throw selectError;

  // #696 verify pass — the offer re-presents itself, fully pre-checked, every
  // time she reopens a saved plan (GardenPlannerTool reloads it from
  // localStorage and resets the selection). Without this filter a second tap
  // appends a second "Water Tomatoes" to the same row, and the scheduler then
  // nudges her twice for one plant. Dedupe lives HERE, not only in the UI, so
  // a double-tap or a stale UI read can't get around it.
  const alreadyTracked = new Set(plantNamesFromConfig(existingTask?.config).map(normalizePlantName));
  const skipped = inputs.filter((i) => alreadyTracked.has(normalizePlantName(i.plantName))).map((i) => i.plantName);
  const fresh = inputs.filter((i) => !alreadyTracked.has(normalizePlantName(i.plantName)));
  // Nothing new to write. Returning the existing row's id (not "") keeps this
  // a success the caller can point at Tasks, without a no-op write.
  if (fresh.length === 0) return { taskId: existingTask?.id || "", items: [], skipped };

  const todayIso = new Date().toISOString().slice(0, 10);
  const newItems = fresh.map((input) => ({
    id: crypto.randomUUID().slice(0, 8),
    name: `Water ${input.plantName}`,
    category: "water" as const,
    interval_days: input.waterIntervalDays,
    last_done: todayIso,
    remind_days_before: 1,
    plant_name: input.plantName,
    completion_history: [],
    learned_interval: null,
  }));
  // location: "Garden" — buildPlantFromForm's field, needed for the row
  // shape; Garden Planner doesn't distinguish indoor/outdoor per crop, and
  // "Garden" is the closest honest default for a planting-calendar plan.
  const newPlants = fresh.map((input) => ({
    name: input.plantName,
    location: "Garden",
    harvest_date: null,
  }));

  // Describes what this call wrote, so the caller's confirmation can't claim
  // more than landed (house rule 16).
  const resultItems: CreatedPlantCareItem[] = fresh.map((input) => ({
    plantName: input.plantName,
    waterIntervalDays: input.waterIntervalDays,
  }));

  if (existingTask) {
    const items = [...(existingTask.config?.items || []), ...newItems];
    const plants = [...(existingTask.config?.plants || []), ...newPlants];
    const { data, error } = await (supabase as any)
      .from("scheduled_tasks")
      .update({ config: { ...(existingTask.config || {}), items, plants }, updated_at: new Date().toISOString() })
      .eq("id", existingTask.id)
      .select("id");
    if (error) throw error;
    assertWriteLanded(data, "plant care reminder");
    return { taskId: existingTask.id, items: resultItems, skipped };
  }

  const { data, error } = await (supabase as any)
    .from("scheduled_tasks")
    .insert({
      user_id: userId,
      task_type: "plant_care",
      display_name: "Plant Care",
      schedule_day: null,
      schedule_time: "07:00",
      interval_days: 1,
      is_active: true,
      config: { plants: newPlants, items: newItems },
      next_run_at: new Date().toISOString(),
    })
    .select("id");
  if (error) throw error;
  assertWriteLanded(data, "plant care reminder");
  return { taskId: data[0].id, items: resultItems, skipped };
}
